/**
 * The sentences the UI uses to explain a MISSING permission.
 *
 * This exists because of a deliberate reversal in how this product presents
 * denial. The UI used to HIDE what you could not reach — a tab vanished, a
 * button was never rendered. It now shows the surface and explains the gap:
 * "You do not have permission to read files".
 *
 * Two things this file is NOT:
 *
 *  - It is not a security boundary. `requirePermission` on the route is, and
 *    always was. Hiding a control was only ever a courtesy; removing that
 *    courtesy changes nothing about what the server will accept. Every client
 *    check that used to hide is KEPT — it now decides which sentence to show
 *    instead of whether to render at all.
 *  - It is not for other people's records. A phrase here names a FEATURE
 *    ("files", "cards", "roles"), never an instance. Saying "you cannot view
 *    project Harbor Realty Rebrand" would disclose that the project exists
 *    and what it is called, to someone the backend deliberately answers 404
 *    for. Records the caller has no grant for stay hidden and unnamed; only
 *    the tab that would have contained them becomes visible.
 *
 * A phrase is `<verb> <subject>` — a verb from the permission level and a
 * subject from the resource. Callers may override either for a specific
 * action, which is how "add cards" is produced from `PROJECT_CARD: edit`
 * (whose default phrase, "edit cards", is right for a card body and wrong for
 * an Add Card button).
 */

import {
  PERMISSION_CATALOG,
  PermissionLevel,
  PermissionResource,
} from './permission-catalog';

/**
 * The default verb per level. Deliberately the plainest possible reading of
 * the ladder — a resource that wants something better overrides it below.
 */
const LEVEL_VERBS: Record<PermissionLevel, string> = {
  [PermissionLevel.VIEW]: 'view',
  [PermissionLevel.EDIT]: 'edit',
  [PermissionLevel.MANAGE]: 'manage',
};

/**
 * Per-resource verb overrides, for the cases where the generic level verb
 * reads badly. Kept small on purpose: every entry here is a phrase a human
 * would actually say, not a synonym for its own sake.
 *
 * `Partial` on both levels — a resource only lists the levels it wants to
 * change, and falls back to `LEVEL_VERBS` for the rest.
 */
const RESOURCE_LEVEL_VERBS: Partial<
  Record<PermissionResource, Partial<Record<PermissionLevel, string>>>
> = {
  // "view files" is technically right but nobody says it; you read a file.
  [PermissionResource.FILE]: { [PermissionLevel.VIEW]: 'read' },
  [PermissionResource.WIKI]: { [PermissionLevel.VIEW]: 'read' },
  [PermissionResource.KNOWLEDGE]: { [PermissionLevel.VIEW]: 'read' },
  // Observability is a dashboard, not a document — you see it.
  [PermissionResource.OBSERVABILITY]: { [PermissionLevel.VIEW]: 'see' },
  [PermissionResource.EXTERNAL_CONVERSATION]: {
    [PermissionLevel.VIEW]: 'read',
  },
  [PermissionResource.TEAM_CHAT]: { [PermissionLevel.VIEW]: 'read' },
};

/**
 * The noun phrase per resource: what the sentence is ABOUT.
 *
 * Not derived from `RESOURCE_LABELS` by lowercasing, because the labels are
 * column headings ("Network Policy", "Members") and several of them need an
 * article or a different plural to survive being dropped into a sentence
 * ("the network policy", "team members"). A full `Record` rather than a
 * `Partial`, so adding a 31st `PermissionResource` is a compile error here
 * rather than a sentence that trails off — see the completeness test.
 */
const RESOURCE_SUBJECTS: Record<PermissionResource, string> = {
  [PermissionResource.PROJECT]: 'projects',
  [PermissionResource.PROJECT_CARD]: 'cards',
  [PermissionResource.PROJECT_COMMENT]: 'card comments',
  [PermissionResource.EPIC]: 'epics',
  [PermissionResource.SPRINT]: 'sprints',
  [PermissionResource.WIKI]: 'the wiki',
  [PermissionResource.DATABASE]: 'databases',
  [PermissionResource.FILE]: 'files',
  [PermissionResource.AGENT]: 'agents',
  [PermissionResource.SKILL]: 'skills',
  [PermissionResource.CUSTOM_TOOL]: 'custom tools',
  [PermissionResource.AGENT_TEST]: 'agent tests',
  [PermissionResource.KNOWLEDGE]: 'knowledge sources',
  [PermissionResource.TEAM_SECRET]: 'team secrets',
  [PermissionResource.WORKSPACE]: 'workspaces',
  [PermissionResource.NETWORK_POLICY]: 'the network policy',
  [PermissionResource.SKILLS_POLICY]: 'the skills policy',
  [PermissionResource.SSO]: 'single sign on',
  [PermissionResource.DOMAIN]: 'claimed domains',
  [PermissionResource.MODEL_PROVIDER]: 'model providers',
  [PermissionResource.CHANNEL]: 'channels',
  [PermissionResource.SCHEDULED_TASK]: 'scheduled tasks',
  [PermissionResource.EXTERNAL_WORK]: 'external work',
  [PermissionResource.TEAM_CHAT]: 'team chat',
  [PermissionResource.EXTERNAL_CONVERSATION]: 'external conversations',
  [PermissionResource.APPROVAL]: 'approvals',
  [PermissionResource.OBSERVABILITY]: 'metrics and logs',
  [PermissionResource.TEAM_SETTINGS]: 'team settings',
  [PermissionResource.TEAM_MEMBER]: 'team members',
  [PermissionResource.ROLE]: 'roles',
  [PermissionResource.BILLING]: 'billing',
};

/**
 * What a caller may override for one specific action. Both are optional and
 * independent: the Add Card button overrides only the verb (`'add'`) and
 * keeps the resource's own subject (`'cards'`), so the two halves cannot
 * drift out of agreement with each other.
 */
export interface PermissionPhraseOverrides {
  /** e.g. `'add'` on a `PROJECT_CARD: edit` Add Card button. */
  verb?: string;
  /** e.g. `'archived projects'` where the plain resource noun is too broad. */
  subject?: string;
}

export interface PermissionPhraseInput extends PermissionPhraseOverrides {
  resource: PermissionResource;
  level: PermissionLevel;
}

/**
 * Last-resort subject for a resource with no entry above. Unreachable through
 * the type system (`RESOURCE_SUBJECTS` is a total `Record`), but a phrase that
 * silently becomes "You do not have permission to view " is a worse failure
 * than an ugly one, so `project_card` degrades to "project card" rather than
 * to nothing.
 */
function fallbackSubject(resource: PermissionResource): string {
  return String(resource).replace(/_/g, ' ').trim();
}

/** The verb for `level` on `resource`, honouring per-resource overrides. */
export function permissionVerb(
  resource: PermissionResource,
  level: PermissionLevel,
): string {
  return (
    RESOURCE_LEVEL_VERBS[resource]?.[level] ||
    LEVEL_VERBS[level] ||
    String(level)
  );
}

/** The noun phrase for `resource`. */
export function permissionSubject(resource: PermissionResource): string {
  return RESOURCE_SUBJECTS[resource] || fallbackSubject(resource);
}

/**
 * `<verb> <subject>` — e.g. `read files`, `manage roles`, or `add cards` when
 * the caller overrides the verb.
 */
export function permissionPhrase({
  resource,
  level,
  verb,
  subject,
}: PermissionPhraseInput): string {
  const resolvedVerb = (verb || permissionVerb(resource, level)).trim();
  const resolvedSubject = (subject || permissionSubject(resource)).trim();
  return [resolvedVerb, resolvedSubject].filter(Boolean).join(' ');
}

/**
 * The sentence the human partner asked for, verbatim in shape:
 * "You do not have permission to read files".
 *
 * No trailing period — it is rendered as a heading-weight line, and callers
 * that need it mid-sentence punctuate for themselves.
 */
export function permissionDeniedMessage(input: PermissionPhraseInput): string {
  return `You do not have permission to ${permissionPhrase(input)}`;
}

/**
 * The grant to ask for, in the same `Resource: level` form the roles screens
 * already use ("Roles: edit"). This is the actionable half of the notice —
 * the sentence says what you cannot do, this says what to request.
 */
export function permissionRequirementLabel(
  resource: PermissionResource,
  level: PermissionLevel,
): string {
  const label =
    PERMISSION_CATALOG[resource]?.label || fallbackSubject(resource);
  return `${label}: ${level}`;
}
